import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import Coachmark from './Coachmark';
import TipsPanel from './TipsPanel';
import TipsTrigger from './TipsTrigger';
import { useTipProgress } from './TipProgressContext';
import { useTourController } from './useTour';
import type {
  ContextualTip,
  TipRegistry,
  TipTokenPrefix,
  TipTour,
  TipWhenContext,
} from './types';

type TipsUiValue = {
  tokenPrefix: TipTokenPrefix;
  openPanel: () => void;
  closePanel: () => void;
  panelOpen: boolean;
};

const TipsUiContext = createContext<TipsUiValue | null>(null);

export const useTipsUi = (): TipsUiValue => {
  const ctx = useContext(TipsUiContext);
  if (!ctx) {
    throw new Error('useTipsUi must be used within ProductTipsHost');
  }
  return ctx;
};

export type ProductTipsHostProps = {
  tokenPrefix: TipTokenPrefix;
  registry: TipRegistry;
  resolveVisible: (ctx: TipWhenContext) => {
    tours: TipTour[];
    tips: ContextualTip[];
    autoTourId?: string | null;
  };
  whenContext: Omit<TipWhenContext, 'pathname' | 'asPath'>;
  /** When false, do not auto-start (e.g. Melon onboarding still running). */
  allowAutoStart?: boolean;
  children?: ReactNode;
};

export const ProductTipsHost = ({
  tokenPrefix,
  registry,
  resolveVisible,
  whenContext,
  allowAutoStart = true,
  children,
}: ProductTipsHostProps) => {
  const router = useRouter();
  const { ready, isTourDone } = useTipProgress();
  const [panelOpen, setPanelOpen] = useState(false);
  /** Tours finished or skipped this session — never auto-restart them. */
  const blockedAutoRef = useRef<Set<string>>(new Set());
  /** Auto-start at most one tour id per surface mount. */
  const autoStartedRef = useRef<string | null>(null);

  const ctx: TipWhenContext = useMemo(
    () => ({
      pathname: router.pathname,
      asPath: router.asPath,
      ...whenContext,
    }),
    [router.pathname, router.asPath, whenContext]
  );

  const visible = useMemo(() => resolveVisible(ctx), [resolveVisible, ctx]);

  const { active, startTour, replayTour, next, back, skip } = useTourController(
    registry.tours
  );

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const finishAndBlock = useCallback(
    (handler: () => void) => {
      const tourId = active?.tour.id;
      if (tourId) blockedAutoRef.current.add(tourId);
      handler();
    },
    [active]
  );

  const onNext = useCallback(() => {
    const tourId = active?.tour.id;
    const isLast =
      active != null && active.stepIndex >= active.tour.steps.length - 1;
    if (tourId && isLast) blockedAutoRef.current.add(tourId);
    next();
  }, [active, next]);

  const onSkip = useCallback(() => {
    finishAndBlock(skip);
  }, [finishAndBlock, skip]);

  useEffect(() => {
    if (!ready || !allowAutoStart || active || panelOpen) return;
    const tourId = visible.autoTourId;
    if (!tourId) return;
    if (blockedAutoRef.current.has(tourId)) return;
    if (autoStartedRef.current === tourId) return;
    const tour = registry.tours.find((t) => t.id === tourId);
    if (!tour || !tour.autoStart) return;
    if (isTourDone(tour.id, tour.version)) {
      blockedAutoRef.current.add(tour.id);
      return;
    }
    autoStartedRef.current = tourId;
    startTour(tour.id);
  }, [
    ready,
    allowAutoStart,
    active,
    panelOpen,
    visible.autoTourId,
    registry.tours,
    isTourDone,
    startTour,
  ]);

  const uiValue = useMemo(
    () => ({
      tokenPrefix,
      openPanel,
      closePanel,
      panelOpen,
    }),
    [tokenPrefix, openPanel, closePanel, panelOpen]
  );

  const step = active?.tour.steps[active.stepIndex] ?? null;

  return (
    <TipsUiContext.Provider value={uiValue}>
      {children}
      <TipsPanel
        tokenPrefix={tokenPrefix}
        open={panelOpen}
        onClose={closePanel}
        tips={visible.tips}
        tours={visible.tours}
        isTourDone={isTourDone}
        onReplayTour={(id) => {
          blockedAutoRef.current.delete(id);
          autoStartedRef.current = null;
          closePanel();
          replayTour(id);
        }}
        onStartTour={(id) => {
          blockedAutoRef.current.delete(id);
          closePanel();
          startTour(id);
        }}
      />
      <Coachmark
        tokenPrefix={tokenPrefix}
        step={step}
        stepIndex={active?.stepIndex ?? 0}
        stepCount={active?.tour.steps.length ?? 0}
        onNext={onNext}
        onBack={back}
        onSkip={onSkip}
      />
    </TipsUiContext.Provider>
  );
};

/** Toolbar tip control — must sit under ProductTipsHost. */
export const ShellTipsTrigger = ({ compact }: { compact?: boolean }) => {
  const { tokenPrefix, openPanel } = useTipsUi();
  return (
    <TipsTrigger
      tokenPrefix={tokenPrefix}
      onClick={openPanel}
      compact={compact}
    />
  );
};

export default ProductTipsHost;
